import React, {useCallback, useState} from 'react';
import clsx from 'clsx';
import {createPortal} from 'react-dom';
import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	DragOverlay,
	type DragStartEvent,
	KeyboardSensor,
	PointerSensor,
	TouchSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	arrayMove,
	rectSortingStrategy,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import {EmptyStateFallbackMessage} from '@netx/core-components/EmptyStateFallbackMessage';
import type {Theme} from '@netx/core-components/Theme';
import {i18n} from '@netx/core/i18n';
import {IAttributeSet} from '../../types';
import type {AttributeSetId, AttributeSetTemplates} from '../../types';
import SortableSetComponent, {
	type SortableAttributeSetComponentProps,
} from '../SortableSet';

const styles = (theme: Theme) =>
	createStyles({
		root: {
			display: 'flex',
			position: 'relative',
			overflow: 'auto',
			marginTop: theme.spacing(2),
			padding: '0 16px',
			flex: '1 1 auto',
			flexWrap: 'nowrap',
			border: `1px solid transparent`,
			'&:not($hasMultipleItems), &$list': {
				flexDirection: 'column',
				'& $item, & .sortable-set': {
					flex: '0 0 auto',
					width: '100%',
					marginBottom: 16,
				},
			},
			'&$list $itemWrapper': {
				flexDirection: 'column',
				flexFlow: 'initial',
			},
			'&:not($list)$hasMultipleItems': {
				'& $item, & .sortable-set': {
					flex: '0 0 auto',
					width: 'calc(50% - 9px)',
					marginRight: 16,
				},
			},
		},
		item: {
			flex: '0 0 auto',
			width: 'calc(50% - 9px)',
		},
		itemWrapper: {
			alignItems: 'flex-start',
			display: 'flex',
			position: 'relative',
			flex: '1 1 auto',
			flexWrap: 'nowrap',
			maxWidth: '100%',
		},
		hasMultipleItems: {},
		list: {},
		column: {},
		dragOverlay: {
			cursor: 'grabbing',
			borderRadius: 4,
			boxShadow:
				theme.palette.mode === 'light'
					? `-1px 0 15px 0 rgba(34, 33, 81, 0.01), 0px 15px 15px 0 rgba(34, 33, 81, 0.25)`
					: `-1px 0 15px 0 rgba(0, 0, 0, 0.01), 0px 15px 15px 0 rgba(0, 0, 0, 0.25)`,
		},
	});

export interface SortableAttributeSetsProps extends Omit<
	SortableAttributeSetComponentProps,
	'attributes' | 'id' | 'title'
> {
	ids: AttributeSetId[];
	attributeTemplates: AttributeSetTemplates;
	sets: IAttributeSet[];
	orderUpdated(ids: AttributeSetId[]): void;
	layout?: string;
	enforce?: string[];
	setsLoaded?: AttributeSetId[];
	inputForm: any;
}

const SortableAttributeSetsComponent = ({
	classes,
	enforce,
	ids,
	attributeTemplates,
	sets,
	setsLoaded,
	layout = 'list',
	loading,
	orderUpdated,
	...props
}: WithStyles<typeof styles> & SortableAttributeSetsProps) => {
	const [activeId, setActiveId] = useState<string | null>(null);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
		useSensor(TouchSensor),
	);

	const handleDragEnd = useCallback(
		({active, over}: DragEndEvent) => {
			setActiveId(null);
			if (over && active.id !== over.id) {
				const oldIndex = ids.indexOf(active.id as any);
				const newIndex = ids.indexOf(over.id as any);
				orderUpdated(arrayMove(ids, oldIndex, newIndex));
			}
		},
		[ids],
	);

	const handleDragStart = useCallback(({active}: DragStartEvent) => {
		setActiveId(active.id as any);
	}, []);

	if (!loading && (!sets.length || !ids.length)) {
		return (
			<EmptyStateFallbackMessage
				title={i18n.t('i18n.noAttributeSetsSelected')}
			/>
		);
	}

	return (
		<div
			className={clsx(classes.root, {
				[classes.hasMultipleItems]: ids.length > 1,
				// @ts-ignore
				[classes[layout]]: true,
			})}
		>
			<DndContext
				autoScroll
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragCancel={() => setActiveId(null)}
				onDragEnd={handleDragEnd}
				onDragStart={handleDragStart}
				// layoutMeasuring={{strategy: LayoutMeasuringStrategy.WhileDragging}}
			>
				<SortableContext
					items={ids}
					strategy={
						layout === 'list'
							? verticalListSortingStrategy
							: rectSortingStrategy
					}
				>
					<div className={classes.itemWrapper}>
						{ids.map((id, index) => (
							<SortableSetComponent
								key={id}
								disableSort={enforce && enforce.includes(id)}
								title={sets.find((set) => set.id === id)?.name}
								attributes={attributeTemplates[id]}
								id={id}
								{...props}
								loading={!setsLoaded?.includes(id) || loading}
							/>
						))}
					</div>
				</SortableContext>

				{createPortal(
					<DragOverlay zIndex={9999}>
						{activeId != null ? (
							<div className={classes.dragOverlay}>
								<SortableSetComponent
									title={sets.find((set) => set.id === activeId)?.name}
									{...(props as SortableAttributeSetComponentProps)}
									attributes={attributeTemplates[activeId]}
								/>
							</div>
						) : null}
					</DragOverlay>,
					document.body,
				)}
			</DndContext>
		</div>
	);
};

export const SortableAttributeSets = withStyles(styles)(
	SortableAttributeSetsComponent as any,
);
export default SortableAttributeSets;
